/**
 * 안전자재 분야별 물품 목록 (품목 단위 가이드)
 */
import { getCategoryById } from './safetyMaterialsCategories';
import { smartEquipmentItems } from './smartEquipmentItems';
import {
  buildCertifiedPpeManagementCost,
  buildSafetyFacilityManagementCost,
  buildConditionalFacilityCost,
  COST_LINK_NA2,
} from './materialsManagementCost';

export const safetyMaterialsItemsByCategory = {
  scaffold: [
    {
      id: 'tubeScaffold',
      name: '강관비계',
      description: '강관·받침·연결재 등으로 조립하는 비계',
      keywords: ['강관비계', '비계', '조립'],
      managementCost: buildConditionalFacilityCost({
        summary: '비계 자재 전체가 아니라, 추락·낙하 방지를 위한 안전시설 부분에 한해 안전시설비로 쓸 수 있는지 현장별로 확인합니다.',
      }),
    },
    {
      id: 'systemScaffold',
      name: '시스템비계',
      description: '시스템 부재로 조립하는 비계',
      keywords: ['시스템비계', '비계'],
      managementCost: buildConditionalFacilityCost({
        summary: '시스템비계 구조물 중 안전난간·발끝막이·추락방호 등 안전시설에 해당하는 비용은 안전시설비로 볼 수 있습니다.',
      }),
    },
    {
      id: 'workPlatform',
      name: '작업발판',
      description: '비계·거푸집 등의 작업을 위한 발판',
      keywords: ['작업발판', '발판'],
      managementCost: buildConditionalFacilityCost({
        summary:
          '공사 수행용 작업발판·통로와 구분되어, 추락·전도·찔림 등 재해 예방 목적의 발판·실족방지망 등은 안전시설비로 인정될 수 있습니다.',
        links: [
          COST_LINK_NA2,
          {
            label: '질의회시 1-1-75 — 승강기 작업용 작업발판',
            url: '/safety-regulations/qa-construction/chapter1/section1/75',
          },
        ],
      }),
    },
    {
      id: 'guardrail',
      name: '안전난간',
      description: '추락 방지를 위한 난간·추락방호망 설치',
      keywords: ['안전난간', '난간'],
      managementCost: buildSafetyFacilityManagementCost({
        note: '비계·작업발판·개구부 등에 설치하는 안전난간·추락방호망 구입·설치 비용은 안전시설비로 사용할 수 있습니다.',
        links: [
          COST_LINK_NA2,
          {
            label: '질의회시 1-1-74 — 비계 안전난간 설치비',
            url: '/safety-regulations/qa-construction/chapter1/section1/74',
          },
        ],
      }),
    },
    {
      id: 'toeBoard',
      name: '발끝막이판',
      description: '자재·공구 낙하 방지용 발끝막이',
      keywords: ['발끝막이판', '발끝막이'],
      managementCost: buildSafetyFacilityManagementCost({
        note: '낙하·비래물 방지를 위한 발끝막이판 설치는 안전시설비에 해당할 수 있습니다.',
      }),
    },
    {
      id: 'ladder',
      name: '사다리',
      description: '이동식·고정식 사다리 사용·설치',
      keywords: ['사다리', '이동식사다리'],
      managementCost: buildConditionalFacilityCost({
        summary: '사다리 구입·임대가 안전시설비인지는 사용 목적·고시 해석에 따라 달라질 수 있어 질의회시·현장 확인이 필요합니다.',
      }),
    },
  ],
  fall: [
    {
      id: 'fullBodyHarness',
      name: '전신형 안전대',
      description: '추락 시 체중을 지지하는 전신형 보호구',
      keywords: ['안전대', '전신형', '추락'],
      managementCost: buildCertifiedPpeManagementCost({
        note: '안전인증을 받은 전신형 안전대는 보호구(고시 제7조제1항제3호)로 안전관리비 사용이 가능합니다.',
      }),
    },
    {
      id: 'lanyard',
      name: '연결줄·충격흡수기',
      description: '안전대와 구조물·추락방지기 연결',
      keywords: ['연결줄', '충격흡수기', '안전대'],
      managementCost: buildCertifiedPpeManagementCost({
        note: '안전대와 함께 사용하는 연결줄·충격흡수기 등 안전인증 보호구는 보호구 항목으로 사용할 수 있습니다.',
      }),
    },
    {
      id: 'fallArrester',
      name: '추락방지기',
      description: '추락 시 자동 잠금·제동 장치',
      keywords: ['추락방지기', '안전블록'],
      managementCost: buildCertifiedPpeManagementCost({
        note: '추락방지기(안전블록 등)가 안전인증 보호구에 해당하면 보호구 항목으로 사용할 수 있습니다.',
      }),
    },
    {
      id: 'openingCover',
      name: '개구부 방호',
      description: '개구부 덮개·난간 등 추락 방지',
      keywords: ['개구부', '추락방지'],
      managementCost: buildSafetyFacilityManagementCost({
        note: '개구부 덮개·안전난간·추락방호망 등 추락·낙하 방지를 위한 안전시설 구입·설치 비용은 안전시설비로 사용할 수 있습니다.',
        links: [
          COST_LINK_NA2,
          {
            label: '질의회시 1-1-35 — 말뚝 상부 덮개(개구부 방호)',
            url: '/safety-regulations/qa-construction/chapter1/section1/35',
          },
        ],
      }),
    },
    {
      id: 'safetyNet',
      name: '안전망',
      description: '추락·낙하물 방지용 안전망',
      keywords: ['안전망', '추락'],
      managementCost: buildSafetyFacilityManagementCost({
        note: '추락방호망·낙하물 방지망 설치·구입 비용은 안전시설비(추락·낙하 방지용 시설)로 사용할 수 있습니다.',
        links: [
          COST_LINK_NA2,
          {
            label: '질의회시 1-1-33 — 추락안전매트',
            url: '/safety-regulations/qa-construction/chapter1/section1/33',
          },
        ],
      }),
    },
  ],
  machine: [
    {
      id: 'press',
      name: '프레스',
      description: '프레스 방호장치·안전조작',
      keywords: ['프레스', '방호장치'],
      managementCost: buildSafetyFacilityManagementCost({
        note: '기계에 일체 제작된 방호장치 부분의 가액 등, 산업재해 예방용 방호장치는 안전시설비 가목에 해당할 수 있습니다.',
        limitedRecognition: true,
      }),
    },
    {
      id: 'grinder',
      name: '연삭기',
      description: '연삭숫돌·방호덮개·보안경',
      keywords: ['연삭기', '방호장치'],
      managementCost: buildConditionalFacilityCost({
        summary: '연삭기 방호덮개 등 방호장치 설치비는 안전시설비로 볼 수 있으나, 보안경 등 보호구는 별도(보호구 항목)입니다.',
      }),
    },
    {
      id: 'conveyor',
      name: '컨베이어',
      description: '컨베이어 위험부 방호',
      keywords: ['컨베이어', '설비'],
      managementCost: buildSafetyFacilityManagementCost({
        note: '컨베이어 위험부 방호장치 설치·구입은 안전시설·방호장치 비용으로 검토할 수 있습니다.',
        limitedRecognition: true,
      }),
    },
    {
      id: 'woodworking',
      name: '목재가공기계',
      description: '원형톱·표면플레너 등 방호',
      keywords: ['목재가공', '원형톱'],
      managementCost: buildSafetyFacilityManagementCost({
        limitedRecognition: true,
        note: '원형톱·표면플레너 등 위험부 방호장치는 안전시설·방호장치로 인정될 수 있습니다.',
      }),
    },
  ],
  electric: [
    {
      id: 'portableWelder',
      name: '이동형 용접기',
      description: '용접 작업 전기·화재 안전',
      keywords: ['용접기', '용접', '전기'],
      managementCost: buildConditionalFacilityCost({
        summary: '용접 화재 예방용 소화기·방호는 고시 제2호 다목(소화기) 등과 연계해 확인하세요.',
      }),
    },
    {
      id: 'fireExtinguisher',
      name: '소화기',
      description: '소화기 비치·점검·사용',
      keywords: ['소화기', '화재'],
      managementCost: buildSafetyFacilityManagementCost({
        category: '안전시설비 · 소화기',
        note: '용접 등 화재 위험작업 시 사용하는 소화기 구입·임대비는 고시 제7조제1항제2호 다목으로 사용할 수 있습니다.',
        links: [
          COST_LINK_NA2,
          {
            label: '질의회시 1-1-47 — 소화기 구입·임대',
            url: '/safety-regulations/qa-construction/chapter1/section1/47',
          },
        ],
      }),
    },
    {
      id: 'gasDetector',
      name: '가스측정기',
      description: '산소·유해가스 농도 측정',
      keywords: ['가스측정', '밀폐공간'],
      managementCost: buildConditionalFacilityCost({
        summary: '밀폐공간·유해가스 측정 장비는 스마트안전장비·위험성평가 항목 등과 구분해 해당 여부를 확인합니다.',
      }),
    },
    {
      id: 'groundFault',
      name: '누전차단기',
      description: '임시전기·누전·접지',
      keywords: ['누전차단기', '임시전기'],
      managementCost: buildConditionalFacilityCost({
        summary: '임시전기 안전시설·누전차단기는 전기 안전 조치 목적·비용 성격에 따라 질의회시로 확인합니다.',
      }),
    },
  ],
  lifting: [
    {
      id: 'mobileCrane',
      name: '이동식 크레인',
      description: '크레인 설치·운전·신호',
      keywords: ['크레인', '양중'],
      managementCost: buildConditionalFacilityCost({
        summary: '양중 작업 안전조치·신호·펜스 등은 안전시설·교통안전시설로 볼 수 있는 부분이 있어 목적별로 확인합니다.',
      }),
    },
    {
      id: 'towerCrane',
      name: '타워크레인',
      description: '타워크레인 설치·해체·운전',
      keywords: ['타워크레인'],
      managementCost: buildConditionalFacilityCost({
        summary: '타워크레인 작업장 안전펜스·신호 등 예방 목적 비용은 안전시설비 해당 여부를 확인합니다.',
      }),
    },
    {
      id: 'wireRopeSling',
      name: '와이어로프·섬유로프',
      description: '줄걸이·슬링 사용',
      keywords: ['와이어로프', '섬유로프', '줄걸이'],
      managementCost: buildConditionalFacilityCost({
        summary: '양중용 줄걸이·슬링이 안전관리비 항목인지는 본공사 자재와 구분·질의회시 확인이 필요합니다.',
      }),
    },
    {
      id: 'forklift',
      name: '지게차',
      description: '지게차 운전·하역',
      keywords: ['지게차', '하역'],
      managementCost: buildConditionalFacilityCost({
        summary: '지게차 방호장치 설치비 등은 질의회시(안전시설비)로 인정 사례가 있습니다.',
        links: [
          COST_LINK_NA2,
          {
            label: '질의회시 1-1-38 — 지게차 방호장치',
            url: '/safety-regulations/qa-construction/chapter1/section1/38',
          },
        ],
      }),
    },
    {
      id: 'hoist',
      name: '호이스트·윈치',
      description: '권상기·체인블록 등',
      keywords: ['호이스트', '윈치', '양중'],
      managementCost: buildConditionalFacilityCost({
        summary: '양중 설비 자체와 안전시설·방호 비용은 구분하여 확인합니다.',
      }),
    },
  ],
  smart: smartEquipmentItems,
};

export const getItemsForCategory = (categoryId) =>
  safetyMaterialsItemsByCategory[categoryId] || [];

export const getItemById = (categoryId, itemId) =>
  getItemsForCategory(categoryId).find((item) => item.id === itemId);

export const getCategoryItemCount = (categoryId) =>
  getItemsForCategory(categoryId).length;

export const isCategoryNavigable = (categoryId) => {
  if (categoryId === 'ppe') return true;
  return getCategoryItemCount(categoryId) > 0;
};

export const getCategoryGuideMeta = (categoryId) => {
  const category = getCategoryById(categoryId);
  const items = getItemsForCategory(categoryId);
  return { category, items, itemCount: items.length };
};
